/**
 * Profile tab: AI review (per search), Summary, Experience, Company Map.
 *
 * Absent data is absent: a person captured before 02's backfill has no
 * summary, location or job descriptions, and those render as nothing (or as
 * an empty-state sentence that does not claim WHY — 03's Blast radius: a null
 * summary is "no row yet" OR "empty About", and the copy must not pick one).
 */
import { useMemo, useState } from "react";

import type {
  PersonAppearance,
  PersonDetail,
  PersonJob,
  PersonReviewRun,
} from "@nu-assist/contracts";

import { Tooltip } from "@nu-assist/ui";

import { Link } from "react-router-dom";
import type { PeopleClient } from "../../clients/people.client";
import { ScoreBadge } from "../../routes/research/import-detail/ScoreBadge";
import { LinkCompanyDialog } from "../company-lists/LinkCompanyDialog";
import { companyPagePath } from "../company-lists/paths";
import { type JobCompany, useJobCompanies } from "../company-lists/useJobCompanies";
import { CompanyMapRunSummary, companyMapRunLine } from "./CompanyMapTab";
import { EmptyLine, PanelSection } from "./PanelSection";
import {
  fmtDate,
  fmtDuration,
  fmtMonth,
  jobSourceLabel,
  monthsBetween,
  personFirstName,
  searchColours,
  searchKey,
  searchLabel,
} from "./format";
import { PANEL_PAGE_SIZE, PanelPager, usePagedList } from "./paging";
import { useJobLogos } from "./useJobLogos";
import { personQueryKey } from "./usePerson";

const SUMMARY_CLAMP_CHARS = 360;
const DESCRIPTION_CLAMP_CHARS = 280;

export function ProfileTab({
  person,
  client,
  onSeeModelCalls,
  onSeeCompanyMapCalls,
}: {
  person: PersonDetail;
  client: PeopleClient;
  /** Switches to the AI Review tab (each run's model call and prompt). */
  onSeeModelCalls?: () => void;
  /** Switches to the Company Map tab (each run's model calls). */
  onSeeCompanyMapCalls?: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <AiReviewSection person={person} onSeeModelCalls={onSeeModelCalls} />
      <SummarySection person={person} />
      <ExperienceSection person={person} client={client} />
      <CompanyMapSection person={person} onSeeCalls={onSeeCompanyMapCalls} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI review
// ---------------------------------------------------------------------------

interface ReviewGroup {
  key: string;
  label: string;
  runs: Array<PersonReviewRun & { listName: string }>;
}

/** Reviews grouped by SEARCH, not flattened: the same person can be a 1.5 on
 *  one search and a 2.2 on another (epic insight), and a flat list would put
 *  the two side by side as if one had replaced the other. */
export function reviewGroups(person: PersonDetail): ReviewGroup[] {
  const groups = new Map<string, ReviewGroup>();
  for (const a of person.appearances ?? []) {
    const runs = a.ai_reviews ?? [];
    if (runs.length === 0) continue;
    const key = searchKey(a);
    const g = groups.get(key) ?? { key, label: searchLabel(a), runs: [] };
    g.runs.push(...runs.map((r) => ({ ...r, listName: a.list_name })));
    groups.set(key, g);
  }
  for (const g of groups.values()) g.runs.sort((x, y) => x.created_at.localeCompare(y.created_at));
  return [...groups.values()];
}

function latestRunAt(g: ReviewGroup): string {
  return g.runs[g.runs.length - 1]?.created_at ?? "";
}

/** Meter fill in twelfths of the 1–3 scale — a width utility, never an inline
 *  style. Full class names so Tailwind's JIT emits them. */
const TWELFTHS = [
  "w-0",
  "w-1/12",
  "w-2/12",
  "w-3/12",
  "w-4/12",
  "w-5/12",
  "w-6/12",
  "w-7/12",
  "w-8/12",
  "w-9/12",
  "w-10/12",
  "w-11/12",
  "w-full",
] as const;

export function meterWidthClass(total: number): string {
  const share = Math.min(1, Math.max(0, (total - 1) / 2));
  return TWELFTHS[Math.round(share * 12)] ?? "w-0";
}

function totalTone(total: number): string {
  return total >= 2.4 ? "bg-success" : total >= 1.7 ? "bg-warning" : "bg-error";
}

function fmtTotal(total: number | null | undefined, digits = 2): string {
  return total == null ? "–" : total.toFixed(digits);
}

function AiReviewSection({
  person,
  onSeeModelCalls,
}: {
  person: PersonDetail;
  onSeeModelCalls?: () => void;
}) {
  const groups = useMemo(() => reviewGroups(person), [person]);
  const colours = useMemo(() => searchColours(person), [person]);
  const newest = useMemo(
    () => [...groups].sort((a, b) => latestRunAt(b).localeCompare(latestRunAt(a)))[0]?.key,
    [groups]
  );
  const [picked, setPicked] = useState<string | null>(null);
  const selected = groups.find((g) => g.key === picked) ?? groups.find((g) => g.key === newest);

  if (!selected) {
    return (
      <PanelSection title="AI review" testId="person-ai-review">
        <EmptyLine testId="person-ai-review-empty">
          No AI review has been run on a list that includes {personFirstName(person)}. Reviews run
          per list, against the search's criteria.
        </EmptyLine>
      </PanelSection>
    );
  }

  const runs = selected.runs;
  const latest = runs[runs.length - 1];
  const previous = runs.length > 1 ? runs[runs.length - 2] : undefined;
  if (!latest) return null;
  const previousScores = new Map((previous?.cells ?? []).map((c) => [c.criterion, c.score]));
  const total = latest.total;

  return (
    <PanelSection
      title="AI review"
      meta={`${runs.length} run${runs.length === 1 ? "" : "s"} on this search`}
      testId="person-ai-review"
    >
      {groups.length > 1 && (
        <fieldset className="mb-3 flex flex-wrap gap-2">
          <legend className="sr-only">Search</legend>
          {groups.map((g) => {
            const last = g.runs[g.runs.length - 1];
            const on = g.key === selected.key;
            return (
              <button
                key={g.key}
                type="button"
                aria-pressed={on}
                data-testid="person-review-search"
                onClick={() => setPicked(g.key)}
                className={`inline-flex items-center gap-2 rounded-pill border px-3 py-1 text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                  on
                    ? "border-accent bg-accent-muted text-accent-ink"
                    : "border-border text-text-secondary hover:border-border-hover"
                }`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${colours.get(g.key)?.dot ?? "bg-chart-neutral"}`}
                />
                {g.label}
                <span className="font-semibold tabular-nums">{fmtTotal(last?.total, 1)}</span>
              </button>
            );
          })}
        </fieldset>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-4 rounded-md border border-border bg-bg p-3">
        <div>
          <div className="text-display tabular-nums text-text" data-testid="person-review-total">
            {fmtTotal(total)}
            <span className="text-body text-text-muted"> / 3</span>
          </div>
          <div
            className="mt-2 h-1.5 w-40 overflow-hidden rounded-full bg-surface-raised"
            aria-hidden="true"
          >
            {total != null && (
              <div
                className={`h-full rounded-full ${totalTone(total)} ${meterWidthClass(total)}`}
              />
            )}
          </div>
        </div>
        <div className="min-w-0 text-caption text-text-secondary">
          <div className="text-label text-text">{selected.label}</div>
          <div>
            v{latest.version} run by {latest.run_by ?? "an unrecorded user"} on{" "}
            {fmtDate(latest.created_at)} · {latest.model_version}
            {previous && ` · ${runs.length} runs, previous total ${fmtTotal(previous.total)}`}
          </div>
          <div>List: {latest.listName}</div>
          {latest.summary_changed_since_review && (
            <div className="mt-1 text-warning-ink" data-testid="person-review-summary-changed">
              Reviewed on an earlier version of the summary
            </div>
          )}
        </div>
      </div>

      <ul className="divide-y divide-border">
        {(latest.cells ?? []).map((cell) => {
          const before = previous ? previousScores.get(cell.criterion) : undefined;
          return (
            <li key={cell.criterion} className="flex gap-3 py-2" data-testid="person-review-cell">
              <ScoreBadge value={cell.score} kind="score" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-2 text-body font-medium text-text">
                  {cell.criterion}
                  {previous && (
                    <CriterionDelta
                      score={cell.score}
                      before={before}
                      version={latest.version}
                      previousVersion={previous.version}
                    />
                  )}
                </div>
                {cell.rationale && (
                  <p className="text-caption text-text-secondary">{cell.rationale}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {onSeeModelCalls && (
        <button
          type="button"
          onClick={onSeeModelCalls}
          // Both cards' links read "See prompts"; the accessible name says which
          // sub-tab each opens, and starts with the visible text (WCAG 2.5.3).
          // The test ids keep their pre-rename names for the E2E specs.
          aria-label="See prompts: AI Review"
          data-testid="person-review-see-model-calls"
          className="mt-2 rounded-sm text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          See prompts
        </button>
      )}
    </PanelSection>
  );
}

function CriterionDelta({
  score,
  before,
  version,
  previousVersion,
}: {
  score: number;
  before: number | undefined;
  version: number;
  previousVersion: number;
}) {
  if (before === undefined) {
    return (
      <span className="text-caption text-info" data-testid="person-review-delta">
        new in v{version}
      </span>
    );
  }
  const d = score - before;
  if (d === 0) return null;
  return (
    <span
      className={`text-caption ${d > 0 ? "text-success-ink" : "text-error-ink"}`}
      data-testid="person-review-delta"
    >
      {d > 0 ? `▲ ${d}` : `▼ ${-d}`} vs v{previousVersion}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

function ClampedText({
  text,
  clampAt,
  clampClass,
  testId,
}: {
  text: string;
  clampAt: number;
  clampClass: string;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const long = text.length > clampAt;
  return (
    <div>
      <p
        data-testid={testId}
        className={`whitespace-pre-line text-body text-text-secondary ${long && !open ? clampClass : ""}`}
      >
        {text}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function SummarySection({ person }: { person: PersonDetail }) {
  const summary = person.identity.summary?.trim();
  return (
    <PanelSection title="Summary" testId="person-summary">
      {summary ? (
        <ClampedText text={summary} clampAt={SUMMARY_CLAMP_CHARS} clampClass="line-clamp-4" />
      ) : (
        <EmptyLine testId="person-summary-empty">
          No summary on file for {personFirstName(person)}.
        </EmptyLine>
      )}
    </PanelSection>
  );
}

// ---------------------------------------------------------------------------
// Experience
// ---------------------------------------------------------------------------

/** The tenure ramp, one hue from LIGHTEST (`bg-tenure-1`, the oldest roles)
 *  to STRONGEST (`bg-tenure-5`, the newest) — tokens `--tenure-1..5`, per
 *  theme. Full class names so Tailwind's JIT emits them. */
export const TENURE_SHADES = [
  "bg-tenure-1",
  "bg-tenure-2",
  "bg-tenure-3",
  "bg-tenure-4",
  "bg-tenure-5",
] as const;

function jobKey(j: PersonJob, i: number): string {
  return j.person_position_id ?? `${j.company ?? ""}|${j.start_month ?? ""}|${j.title ?? ""}|${i}`;
}

/** Dated jobs, oldest first — the strip's order. */
function datedJobs(jobs: PersonJob[]): PersonJob[] {
  return jobs
    .filter((j) => j.start_month)
    .sort((a, b) => (a.start_month ?? "").localeCompare(b.start_month ?? ""));
}

/** Shade class for the segment at `index` of `count` (oldest first), by its
 *  RANK IN TIME, not by company (Jose, 2026-09-22: "lighter = further back").
 *  The newest role takes the strongest step, each older one a step lighter,
 *  and every role past the fifth-newest shares the lightest. */
export function tenureShadeByRank(index: number, count: number): string {
  const fromNewest = count - 1 - index;
  const step = Math.max(0, TENURE_SHADES.length - 1 - fromNewest);
  return TENURE_SHADES[step] ?? "bg-tenure-1";
}

/** `Acme · VP Engineering · Mar 2021 – Jan 2026 · 4 yrs 10 mos`. */
export function tenureLabel(job: PersonJob, months: number): string {
  return [
    job.company,
    job.title,
    `${fmtMonth(job.start_month)} – ${fmtMonth(job.end_month)}`,
    fmtDuration(months),
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The tenure strip: every dated job as a segment whose width is its months,
 *  oldest first, one hue getting stronger toward today. Each segment is a
 *  `@nu-assist/ui` `Tooltip` trigger — focusable, with the role's company,
 *  title, dates and duration in its bubble on hover or keyboard focus.
 *
 *  The width is `flex-grow: <months>` inline: a RUNTIME quantity from the data,
 *  the sanctioned `style` case (SOP 09-pitfalls, like the progress bars'
 *  widths); it cannot be a class. It used to be an SVG (no inline style), but a
 *  `<rect>` cannot host the Tooltip — whose bubble must escape the drawer's
 *  scroll box without a portal (`escapesOverflow`) — and a native `<title>` is
 *  the slow, unstyled, mouse-only tooltip this replaces. */
function TenureStrip({ jobs }: { jobs: PersonJob[] }) {
  const dated = datedJobs(jobs);
  const spans = dated.map((j) => ({
    job: j,
    months: monthsBetween(j.start_month, j.end_month) ?? 1,
  }));
  const total = spans.reduce((s, x) => s + x.months, 0);
  if (total === 0) return null;
  return (
    <div className="mb-3" data-testid="person-tenure">
      <p className="mb-1 text-caption text-text-muted" data-testid="person-tenure-caption">
        Time in each role, oldest → today. Lighter = further back.
      </p>
      <ul
        aria-label="Time in each role, oldest to today"
        // `gap-0.5`: a 2px track-coloured separator between segments, so two
        // neighbouring steps never merge into one bar.
        className="flex h-3 w-full gap-0.5 rounded-full bg-surface-raised"
      >
        {spans.map(({ job, months }, i) => (
          <li
            key={jobKey(job, i)}
            className="flex min-w-0 basis-0"
            // Runtime width — see the component note.
            style={{ flexGrow: months }}
            data-testid="person-tenure-segment"
            data-shade={tenureShadeByRank(i, spans.length)}
          >
            <Tooltip
              content={tenureLabel(job, months)}
              escapesOverflow
              className="h-full w-full"
              triggerClassName="h-full flex-1 self-stretch"
            >
              <span
                className={`block h-full w-full ${tenureShadeByRank(i, spans.length)} ${
                  i === 0 ? "rounded-l-full" : ""
                } ${i === spans.length - 1 ? "rounded-r-full" : ""}`}
              >
                <span className="sr-only">{job.company ?? "Unnamed company"}</span>
              </span>
            </Tooltip>
          </li>
        ))}
      </ul>
      <div className="mt-1 flex justify-between text-caption tabular-nums text-text-muted">
        <span>{dated[0]?.start_month?.slice(0, 4)}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

function listNameById(person: PersonDetail): Map<string, string> {
  return new Map(
    (person.appearances ?? []).map((a: PersonAppearance) => [a.import_id, a.list_name])
  );
}

function shortList(name: string): string {
  return name.length > 34 ? `${name.slice(0, 32)}…` : name;
}

/** A list that saw a job, for the source item's tooltip. */
export interface JobSighting {
  id: string;
  name: string;
  at: string;
}

/** How many lists the source tooltip names before "+ N more". A person seen
 *  in dozens of lists (E2E data locally: every run re-imports the same CSV)
 *  made a tooltip taller than the panel (Jose, local testing, 2026-10-20). */
export const JOB_SOURCE_TOOLTIP_MAX = 5;

/** The tooltip's lists: the most recent first, at most `JOB_SOURCE_TOOLTIP_MAX`,
 *  and how many more there are. Undated sightings sort last. */
export function recentSightings(sightings: JobSighting[]): {
  shown: JobSighting[];
  more: number;
} {
  const newestFirst = [...sightings].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return {
    shown: newestFirst.slice(0, JOB_SOURCE_TOOLTIP_MAX),
    more: Math.max(0, newestFirst.length - JOB_SOURCE_TOOLTIP_MAX),
  };
}

/** The dates line's source item (04): discreet, grey, for audit. Its tooltip
 *  lists the most recent lists that saw the job (see `recentSightings`) and,
 *  when Thrive names the company differently, Thrive's name. */
function JobSource({ job, sightings }: { job: PersonJob; sightings: JobSighting[] }) {
  const label = jobSourceLabel(job.sources);
  if (!label) return null;
  const { shown, more } = recentSightings(sightings);
  return (
    <Tooltip
      escapesOverflow
      clickToPin
      maxWidthClassName="max-w-[280px]"
      content={
        <span className="flex flex-col gap-1 text-left" data-testid="person-job-source-detail">
          {shown.length > 0 ? (
            shown.map((s) => (
              <span key={s.id}>
                {shortList(s.name)} · {fmtDate(s.at)}
              </span>
            ))
          ) : (
            <span>No list on file saw this job.</span>
          )}
          {more > 0 && (
            <span className="text-text-muted" data-testid="person-job-source-more">
              + {more} more {more === 1 ? "list" : "lists"}
            </span>
          )}
          {job.thrive_company && <span>In Thrive: {job.thrive_company}</span>}
        </span>
      }
    >
      <span data-testid="person-job-source">{label}</span>
    </Tooltip>
  );
}

function JobRow({
  job,
  onlyIn,
  sightings,
  logoUrl,
  company,
  onLink,
}: {
  job: PersonJob;
  onlyIn: string | null;
  /** The lists that saw this job, oldest first, for the source tooltip. */
  sightings: JobSighting[];
  /** The company's logo, when ChopLink captured one; initials otherwise. */
  logoUrl?: string;
  /** The company record behind the job, where the company screens are
   *  visible: the company name links to its page. */
  company?: JobCompany;
  /** Offered for a job with no LinkedIn company id, where the company screens
   *  are visible: link it to a company by hand (or change / unlink that). */
  onLink?: () => void;
}) {
  const months = monthsBetween(job.start_month, job.end_month);
  // A URL that fails to load (expired signature, missing object) falls back to
  // the initials rather than leaving an empty square. Remember WHICH URL
  // failed: a refetch brings a fresh one, and that one should show.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showLogo = Boolean(logoUrl) && logoUrl !== failedUrl;
  const initials = (job.company ?? "?")
    .replace(/[^A-Za-z0-9 ]/g, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <li className="flex gap-3 py-3" data-testid="person-job">
      <div
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-surface-raised text-caption font-semibold text-text-secondary"
        data-testid={showLogo ? "person-job-logo" : undefined}
      >
        {showLogo ? (
          <img
            src={logoUrl}
            alt=""
            className="h-full w-full object-contain"
            onError={() => setFailedUrl(logoUrl ?? null)}
          />
        ) : (
          initials || "?"
        )}
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-body font-semibold text-text">
          {job.title ?? "Untitled role"}
          {job.current && (
            <span className="rounded-pill bg-accent-muted px-2 text-micro text-accent-ink">
              Current
            </span>
          )}
          {onlyIn && (
            <span
              className="rounded-pill bg-info-muted px-2 text-micro text-info"
              title={onlyIn}
              data-testid="person-job-only-in"
            >
              only in {shortList(onlyIn)}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-2">
          {job.company &&
            (company ? (
              <Link
                to={companyPagePath(company.company_id)}
                className="text-body text-text hover:text-accent-ink hover:underline"
                data-testid="person-job-company-link"
              >
                {job.company}
              </Link>
            ) : (
              <div className="text-body text-text">{job.company}</div>
            ))}
          {onLink && (
            <button
              type="button"
              onClick={onLink}
              data-testid="person-job-link-company"
              className="text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {job.company_ref ? "Change" : "Link to a company"}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-x-3 text-caption tabular-nums text-text-muted">
          {job.start_month ? (
            <span>
              {fmtMonth(job.start_month)} – {fmtMonth(job.end_month)}
              {months != null && ` · ${fmtDuration(months)}`}
            </span>
          ) : (
            <span>Dates not captured</span>
          )}
          {job.location && <span>{job.location}</span>}
          <JobSource job={job} sightings={sightings} />
        </div>
        {job.description && (
          <div className="mt-1">
            <ClampedText
              text={job.description}
              clampAt={DESCRIPTION_CLAMP_CHARS}
              clampClass="line-clamp-3"
            />
          </div>
        )}
      </div>
    </li>
  );
}

function ExperienceSection({ person, client }: { person: PersonDetail; client: PeopleClient }) {
  const [showStale, setShowStale] = useState(false);
  const jobs = person.experience ?? [];
  const current = jobs.filter((j) => !j.stale);
  const stale = jobs.filter((j) => j.stale);
  const lists = listNameById(person);
  const appearanceAt = new Map((person.appearances ?? []).map((a) => [a.import_id, a.created_at]));
  const sightingsOf = (j: PersonJob): JobSighting[] =>
    (j.seen_in ?? []).map((id) => ({
      id,
      name: lists.get(id) ?? "A deleted list",
      at: appearanceAt.get(id) ?? "",
    }));
  const multiList = (person.appearances ?? []).length > 1;
  // "only in <list>" is a statement about the lists DISAGREEING, so it only
  // means something when there is more than one list to disagree.
  const onlyIn = (j: PersonJob): string | null => {
    if (!multiList || (j.seen_in ?? []).length !== 1) return null;
    const id = j.seen_in?.[0];
    return id ? (lists.get(id) ?? null) : null;
  };
  const partial = multiList ? jobs.filter((j) => (j.seen_in ?? []).length === 1).length : 0;
  const firstStart = jobs
    .map((j) => j.start_month)
    .filter((m): m is string => Boolean(m))
    .sort()[0];
  const career = monthsBetween(firstStart, null);
  const currentPage = usePagedList(current, person.handle);
  const logos = useJobLogos(
    client,
    jobs.map((j) => j.company_id)
  );
  const logoOf = (j: PersonJob) => (j.company_id ? logos.get(j.company_id) : undefined);
  const { enabled: companyScreens, companyOf } = useJobCompanies(jobs);
  const [linking, setLinking] = useState<PersonJob | null>(null);
  // Only a job with no LinkedIn company id can be linked by hand (00 D3 / Q4).
  const linkAction = (j: PersonJob) =>
    companyScreens && j.person_position_id && !j.company_id ? () => setLinking(j) : undefined;
  const stalePage = usePagedList(stale, person.handle);

  if (jobs.length === 0) {
    return (
      <PanelSection title="Experience" testId="person-experience">
        <EmptyLine testId="person-experience-empty">
          No list captured a job history for {personFirstName(person)}.
        </EmptyLine>
      </PanelSection>
    );
  }

  return (
    <PanelSection
      title="Experience"
      meta={`${jobs.length} role${jobs.length === 1 ? "" : "s"}${career ? ` · ${fmtDuration(career)} career` : ""}`}
      testId="person-experience"
    >
      {partial > 0 && (
        <p className="mb-2 text-caption text-info" data-testid="person-experience-merged">
          Merged from {(person.appearances ?? []).length} lists: {partial} of {jobs.length} roles
          appear in only one of them.
        </p>
      )}
      <TenureStrip jobs={current} />
      <ul className="divide-y divide-border">
        {currentPage.items.map((j, i) => (
          <JobRow
            key={jobKey(j, (currentPage.page - 1) * PANEL_PAGE_SIZE + i)}
            job={j}
            onlyIn={onlyIn(j)}
            sightings={sightingsOf(j)}
            logoUrl={logoOf(j) ?? companyOf(j)?.logoUrl ?? undefined}
            company={companyOf(j)}
            onLink={linkAction(j)}
          />
        ))}
      </ul>
      <PanelPager paged={currentPage} noun="role" testId="person-experience-pager" />
      {stale.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowStale((v) => !v)}
            aria-expanded={showStale}
            className="mt-2 text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {showStale
              ? "Hide older variants"
              : `Show ${stale.length} older variant${stale.length === 1 ? "" : "s"}`}
          </button>
          {showStale && (
            <>
              <ul className="divide-y divide-border" data-testid="person-experience-stale">
                {stalePage.items.map((j, i) => (
                  <JobRow
                    key={jobKey(j, (stalePage.page - 1) * PANEL_PAGE_SIZE + i)}
                    job={j}
                    onlyIn={onlyIn(j)}
                    sightings={sightingsOf(j)}
                    logoUrl={logoOf(j) ?? companyOf(j)?.logoUrl ?? undefined}
                    company={companyOf(j)}
                    onLink={linkAction(j)}
                  />
                ))}
              </ul>
              <PanelPager
                paged={stalePage}
                noun="older variant"
                testId="person-experience-stale-pager"
              />
            </>
          )}
        </>
      )}
      {linking?.person_position_id && (
        <LinkCompanyDialog
          positionId={linking.person_position_id}
          jobCompanyName={linking.company ?? null}
          linkedTo={
            linking.company_ref
              ? { company_id: linking.company_ref, name: companyOf(linking)?.name ?? null }
              : null
          }
          personQueryKey={personQueryKey(person.handle)}
          onClose={() => setLinking(null)}
        />
      )}
    </PanelSection>
  );
}

// ---------------------------------------------------------------------------
// Company Map
// ---------------------------------------------------------------------------

function CompanyMapSection({
  person,
  onSeeCalls,
}: {
  person: PersonDetail;
  onSeeCalls?: () => void;
}) {
  const runs = person.company_map ?? [];
  const paged = usePagedList(runs, person.handle);
  return (
    <PanelSection title="Company Map" testId="person-company-map">
      {runs.length === 0 ? (
        <EmptyLine testId="person-company-map-empty">
          No Company Map run has matched {personFirstName(person)}. When one does, this shows which
          target company and role matched, the year window, and who ran it.
        </EmptyLine>
      ) : (
        <ul className="divide-y divide-border">
          {paged.items.map((run) => (
            <li
              key={run.run_id}
              className="py-2 text-body text-text"
              data-testid="person-company-map-run"
            >
              <div className="text-caption text-text-muted">{companyMapRunLine(run)}</div>
              <CompanyMapRunSummary run={run} />
            </li>
          ))}
        </ul>
      )}
      <PanelPager paged={paged} noun="Company Map run" testId="person-company-map-pager" />
      {onSeeCalls && runs.length > 0 && (
        <button
          type="button"
          onClick={onSeeCalls}
          aria-label="See prompts: Company Map"
          data-testid="person-company-map-see-calls"
          className="mt-2 rounded-sm text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          See prompts
        </button>
      )}
    </PanelSection>
  );
}
